import { useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { b2cPolicies } from "./useMsalB2C";
import { jwtDecode, JwtPayload } from "jwt-decode";

interface B2CJwtPayload extends JwtPayload {
  extension_roles?: string;
  impersonationApp?: string;
  tfp?: string;
  extension_afiliacion_pendiente?: string;
}

// Accept MSAL's token rather than depending on its private sessionStorage key format.
export const setToken = (idToken: string) => {
  if (!idToken) {
    throw new Error("MSAL no ha devuelto un token de identidad.");
  }
  const decoded = jwtDecode<B2CJwtPayload>(idToken);
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
    throw new Error("El token de identidad no contiene datos validos.");
  }
  // Validate consumed claims before overwriting the application's existing session.
  for (const claim of [
    decoded.extension_roles, decoded.impersonationApp,
    decoded.tfp, decoded.extension_afiliacion_pendiente,
  ]) {
    if (claim !== undefined && typeof claim !== "string") {
      throw new Error("El token de identidad contiene atributos invalidos.");
    }
  }
  // Preserve the format expected by API consumers; localStorage is NOT isolated per viewer.
  localStorage.setItem("token", JSON.stringify(idToken));
  if (decoded.extension_roles !== undefined) {
    localStorage.setItem("role", decoded.extension_roles);
  } else {
    localStorage.removeItem("role");
  }
  if (checkIsImpersonation(decoded)) {
    localStorage.setItem("isImpersonation", "true");
    if (decoded.impersonationApp) {
      localStorage.setItem("impersonationApp", decoded.impersonationApp);
    } else {
      localStorage.removeItem("impersonationApp");
    }
  } else {
    localStorage.removeItem("isImpersonation");
    localStorage.removeItem("impersonationApp");
  }
  localStorage.setItem("afiliacion_pendiente", String(decoded.extension_afiliacion_pendiente));
  window.dispatchEvent(new Event("afiliacion_pendiente_changed"));
};

export const checkIsImpersonation = (decodedToken: B2CJwtPayload): boolean => {
  return !!decodedToken.impersonationApp ||
    (!!b2cPolicies.names.imp && decodedToken.tfp === b2cPolicies.names.imp);
};

export const useSetToken = () => {
  const { instance } = useMsal();
  const isAuthenticate = useIsAuthenticated();
  const currentAccount = instance.getActiveAccount();
  useEffect(() => {
    if (currentAccount && isAuthenticate) {
      try {
        setToken(currentAccount.idToken ?? "");
      } catch (error: unknown) {
        console.error("Error al obtener el token de MSAL:", error);
      }
    }
  }, [instance, isAuthenticate, currentAccount]);
};
